/**
 * PWA Install Hook
 * Handles install prompt for Android/Chrome and provides iOS detection
 * for Add to Home Screen guidance with Apple-inspired UX
 *
 * Provides a rich install capability model for platform-specific UX:
 * - Platform detection (iOS, Android, Desktop)
 * - Browser detection (Safari, Chrome, Edge, Firefox, Samsung, In-App browsers)
 * - Install method determination (nativePrompt, manualA2HS, manualMenuInstall, unsupported)
 * - Cooldown/dismissal persistence (7 days on dismiss, 24h per show)
 */

import { useState, useEffect, useCallback } from 'react';

// Storage keys
const INSTALL_DISMISSED_KEY = 'postpartner_install_dismissed';
const INSTALL_DISMISSED_UNTIL_KEY = 'postpartner_install_dismissed_until';
const INSTALL_LAST_SHOWN_KEY = 'postpartner_install_last_shown';

// Dismissal duration (7 days)
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
// Cooldown between shows (24 hours)
const SHOW_COOLDOWN_MS = 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/** Platform type */
export type InstallPlatform = 'ios' | 'android' | 'desktop' | 'unknown';

/** Browser type */
export type InstallBrowser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'samsung' | 'inapp' | 'unknown';

/** Install method available */
export type InstallMethod = 'nativePrompt' | 'manualA2HS' | 'manualMenuInstall' | 'unsupported';

export interface PWAInstallCapability {
  /** Device platform */
  platform: InstallPlatform;
  /** Browser being used */
  browser: InstallBrowser;
  /** Method available for installation */
  installMethod: InstallMethod;
  /** Whether installation is possible via any method */
  canInstall: boolean;
  /** Whether the app is currently installed (standalone/fullscreen mode) */
  isInstalled: boolean;
  /** Whether user is in an in-app browser (Instagram, FB, Twitter, etc) */
  isInAppBrowser: boolean;
  /** Internal reason if install unavailable (for debugging) */
  reasonUnavailable?: string;
}

export interface PWAInstallState extends PWAInstallCapability {
  /** Whether the device is iOS (convenience) */
  isIOS: boolean;
  /** Whether Safari is being used on iOS (convenience) */
  isIOSSafari: boolean;
  /** Whether Chrome is being used on iOS (convenience) */
  isIOSChrome: boolean;
  /** Whether Samsung Internet browser is being used (convenience) */
  isSamsungInternet: boolean;
  /** Whether Firefox browser is being used (convenience) */
  isFirefox: boolean;
  /** Trigger the install prompt (Android/Chrome) - returns true if accepted */
  promptInstall: () => Promise<boolean>;
  /** Dismiss the install prompt for 7 days */
  dismissInstall: () => void;
  /** Record that banner was shown (for 24h cooldown) */
  recordShown: () => void;
  /** Whether install was previously dismissed (within 7 days) */
  isDismissed: boolean;
  /** Whether we should show install UI (respects cooldowns + installed state) */
  shouldShowInstall: boolean;
  /** Whether the native beforeinstallprompt is available */
  hasNativePrompt: boolean;
}

/**
 * Detect if running in standalone mode (installed PWA)
 */
function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;

  // Check display-mode media query
  const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
  if (standaloneMediaQuery.matches) return true;

  // Check fullscreen and minimal-ui modes
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return true;

  // iOS Safari standalone mode
  if ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true) {
    return true;
  }

  return false;
}

/**
 * Detect iOS device
 */
function detectIOS(): boolean {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
}

/**
 * Detect platform type
 */
function detectPlatform(): InstallPlatform {
  if (typeof window === 'undefined') return 'unknown';

  const userAgent = window.navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
  if (/android/.test(userAgent)) return 'android';
  if (/windows|macintosh|linux/.test(userAgent) && !/mobile/.test(userAgent)) return 'desktop';

  return 'unknown';
}

/**
 * Detect browser type with comprehensive in-app browser detection
 */
function detectBrowser(): InstallBrowser {
  if (typeof window === 'undefined') return 'unknown';

  const ua = window.navigator.userAgent;

  // Check for in-app browsers FIRST (they often include other browser identifiers)
  // Common in-app browser identifiers from social apps
  const inAppPatterns = [
    /FBAN|FBAV/i,           // Facebook app
    /FB_IAB/i,              // Facebook in-app browser
    /Instagram/i,           // Instagram
    /Twitter/i,             // Twitter/X
    /LinkedInApp/i,         // LinkedIn
    /KAKAOTALK/i,           // KakaoTalk
    /Snapchat/i,            // Snapchat
    /Line\//i,              // LINE
    /Pinterest/i,           // Pinterest
    /Slack/i,               // Slack
    /Telegram/i,            // Telegram
    /WeChat|MicroMessenger/i, // WeChat
    /QQ\//i,                // QQ
    /Discord/i,             // Discord
    /TikTok/i,              // TikTok
    /WhatsApp/i,            // WhatsApp (rare)
    /Threads/i,             // Threads
    /Reddit/i,              // Reddit app
    /GSA/i,                 // Google Search App
  ];

  for (const pattern of inAppPatterns) {
    if (pattern.test(ua)) return 'inapp';
  }

  // WebView detection (Android)
  if (/wv\)|WebView/i.test(ua)) return 'inapp';

  // iOS WebView detection - important to catch after in-app patterns
  // Safari in WebView typically lacks "Safari" in UA or has specific markers
  if (detectIOS()) {
    // iOS UIWebView or WKWebView indicators
    if (/\(iPhone|iPad.*AppleWebKit(?!.*Safari)/i.test(ua)) return 'inapp';
  }

  // Now check for actual browsers
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/EdgiOS|Edg\//i.test(ua)) return 'edge';
  if (/FxiOS|Firefox/i.test(ua) && !/Seamonkey/i.test(ua)) return 'firefox';
  if (/CriOS|Chrome/i.test(ua) && !/Edg/i.test(ua)) return 'chrome';

  // Safari detection (iOS only truly supports Safari for A2HS)
  if (detectIOS() && /Safari/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua)) {
    return 'safari';
  }

  // Desktop Safari
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'safari';

  return 'unknown';
}

/**
 * Detect if Safari on iOS (convenience for existing code)
 */
function detectIOSSafari(): boolean {
  return detectIOS() && detectBrowser() === 'safari';
}

/**
 * Detect if Chrome on iOS (convenience for existing code)
 */
function detectIOSChrome(): boolean {
  return detectIOS() && detectBrowser() === 'chrome';
}

/**
 * Detect if Samsung Internet browser
 */
function detectSamsungInternet(): boolean {
  return detectBrowser() === 'samsung';
}

/**
 * Detect if Firefox browser
 */
function detectFirefox(): boolean {
  return detectBrowser() === 'firefox';
}

/**
 * Detect if running in an in-app browser
 */
function detectInAppBrowser(): boolean {
  return detectBrowser() === 'inapp';
}

/**
 * Determine the install method available for this platform/browser combo
 */
function determineInstallMethod(
  platform: InstallPlatform,
  browser: InstallBrowser,
  hasNativePrompt: boolean
): InstallMethod {
  // Already in standalone = no install needed
  if (isStandaloneMode()) return 'unsupported';

  // In-app browsers can't install - need to redirect to real browser
  if (browser === 'inapp') return 'unsupported';

  // Native prompt available (Chrome/Edge/Samsung on Android, Chrome on desktop)
  if (hasNativePrompt) return 'nativePrompt';

  // iOS - Safari can do Add to Home Screen
  if (platform === 'ios' && browser === 'safari') return 'manualA2HS';

  // iOS - Chrome/Edge can do A2HS since iOS 16.4+
  if (platform === 'ios' && (browser === 'chrome' || browser === 'edge')) return 'manualA2HS';

  // Android Firefox - supports Install via menu
  if (platform === 'android' && browser === 'firefox') return 'manualMenuInstall';

  // Android with unknown browser but no native prompt - try manual menu
  if (platform === 'android') return 'manualMenuInstall';

  // Desktop browsers without native prompt
  if (platform === 'desktop') {
    // Chrome/Edge on desktop typically have beforeinstallprompt
    // If we don't have it, it might not be installable
    if (browser === 'chrome' || browser === 'edge') return 'unsupported';
    // Safari desktop doesn't support PWA install
    if (browser === 'safari') return 'unsupported';
    // Firefox desktop can install via address bar
    if (browser === 'firefox') return 'manualMenuInstall';
  }

  return 'unsupported';
}

/**
 * Check if install was dismissed and still within cooldown period (7 days)
 */
function isInstallDismissed(): boolean {
  try {
    const dismissedUntil = localStorage.getItem(INSTALL_DISMISSED_UNTIL_KEY);
    if (!dismissedUntil) return false;

    const until = parseInt(dismissedUntil, 10);
    return Date.now() < until;
  } catch {
    return false;
  }
}

/**
 * Check if install was shown recently (24h cooldown)
 */
function isInShowCooldown(): boolean {
  try {
    const lastShown = localStorage.getItem(INSTALL_LAST_SHOWN_KEY);
    if (!lastShown) return false;

    const shownAt = parseInt(lastShown, 10);
    return Date.now() - shownAt < SHOW_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/**
 * Mark install as dismissed for 7 days
 */
function markInstallDismissed(): void {
  try {
    const until = Date.now() + DISMISS_DURATION_MS;
    localStorage.setItem(INSTALL_DISMISSED_KEY, 'true');
    localStorage.setItem(INSTALL_DISMISSED_UNTIL_KEY, until.toString());
  } catch {
    // localStorage unavailable
  }
}

/**
 * Record that install UI was shown (for 24h cooldown)
 */
function markInstallShown(): void {
  try {
    localStorage.setItem(INSTALL_LAST_SHOWN_KEY, Date.now().toString());
  } catch {
    // localStorage unavailable
  }
}

// Debug: Expose functions for testing (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as Window & { resetPWAInstall?: () => void }).resetPWAInstall = () => {
    try {
      localStorage.removeItem(INSTALL_DISMISSED_KEY);
      localStorage.removeItem(INSTALL_DISMISSED_UNTIL_KEY);
      localStorage.removeItem(INSTALL_LAST_SHOWN_KEY);
      console.log('[PWA] Install state reset. Refresh the page to see the banner.');
    } catch {
      console.error('Failed to reset PWA install state');
    }
  };

  (window as Window & { debugPWAInstall?: () => void }).debugPWAInstall = () => {
    const platform = detectPlatform();
    const browser = detectBrowser();
    const isInstalled = isStandaloneMode();
    const isDismissed = isInstallDismissed();
    const inCooldown = isInShowCooldown();

    console.log('🔍 PWA Install Debug:');
    console.log('  Platform:', platform);
    console.log('  Browser:', browser);
    console.log('  Is Installed:', isInstalled);
    console.log('  Is Dismissed (7d):', isDismissed);
    console.log('  In Show Cooldown (24h):', inCooldown);
    console.log('  Is In-App Browser:', detectInAppBrowser());
    console.log('  User Agent:', navigator.userAgent);
  };
}

export function usePWAInstall(): PWAInstallState {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isDismissed, setIsDismissed] = useState(() => isInstallDismissed());
  const [isInstalled, setIsInstalled] = useState(() => isStandaloneMode());

  // Detect platform and browser once
  const platform = detectPlatform();
  const browser = detectBrowser();
  const isInAppBrowser = browser === 'inapp';

  // Convenience flags for backward compatibility
  const isIOS = detectIOS();
  const isIOSSafari = detectIOSSafari();
  const isIOSChrome = detectIOSChrome();
  const isSamsungInternet = detectSamsungInternet();
  const isFirefox = detectFirefox();

  // Listen for beforeinstallprompt event (Android/Chrome/Samsung/Edge/Opera)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Listen for display mode changes
  useEffect(() => {
    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    const fullscreenQuery = window.matchMedia('(display-mode: fullscreen)');
    const minimalUiQuery = window.matchMedia('(display-mode: minimal-ui)');

    const handleChange = () => {
      setIsInstalled(standaloneQuery.matches || fullscreenQuery.matches || minimalUiQuery.matches);
    };

    standaloneQuery.addEventListener('change', handleChange);
    fullscreenQuery.addEventListener('change', handleChange);
    minimalUiQuery.addEventListener('change', handleChange);

    return () => {
      standaloneQuery.removeEventListener('change', handleChange);
      fullscreenQuery.removeEventListener('change', handleChange);
      minimalUiQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Determine install method based on capabilities
  const hasNativePrompt = !!deferredPrompt;
  const installMethod = determineInstallMethod(platform, browser, hasNativePrompt);

  // Can install if there's a valid install method
  const canInstall = installMethod !== 'unsupported' && !isInstalled;

  // Build reason if unavailable
  let reasonUnavailable: string | undefined;
  if (isInstalled) {
    reasonUnavailable = 'App is already installed';
  } else if (isInAppBrowser) {
    reasonUnavailable = 'In-app browser detected - open in Safari/Chrome';
  } else if (installMethod === 'unsupported') {
    reasonUnavailable = `Unsupported platform/browser: ${platform}/${browser}`;
  }

  // Should show install: canInstall AND not dismissed AND not in cooldown
  const shouldShowInstall = canInstall && !isDismissed && !isInShowCooldown();

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        return true;
      }
      // User dismissed native prompt - apply cooldown
      markInstallShown();
      return false;
    } catch {
      return false;
    }
  }, [deferredPrompt]);

  const dismissInstall = useCallback(() => {
    markInstallDismissed();
    setIsDismissed(true);
    setDeferredPrompt(null);
  }, []);

  const recordShown = useCallback(() => {
    markInstallShown();
  }, []);

  return {
    // Rich capability model
    platform,
    browser,
    installMethod,
    canInstall,
    isInstalled,
    isInAppBrowser,
    reasonUnavailable,

    // Computed
    shouldShowInstall,
    hasNativePrompt,

    // Convenience flags (backward compat)
    isIOS,
    isIOSSafari,
    isIOSChrome,
    isSamsungInternet,
    isFirefox,

    // Actions
    promptInstall,
    dismissInstall,
    recordShown,
    isDismissed,
  };
}
